import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import LoadingOrder from '@/components/LoadingOrder/LoadingOrder';
import SelectField from '@/components/SelectField';
import {
  checkEtiquetaStatus,
  executeFinalize,
  fetchListaOrcamentos,
  fetchPedidoMotivos,
  leituraPedidoKeys,
} from '@/services/leituraPedidoService';
import type {
  IEtiquetaEnvio,
  IEtiquetaEscaneada,
  IOrcamento,
  IPedidoMotivo,
  TOperacaoLeituraPedido,
} from '@/types/leituraPedido';
import { useAnalytics, useErrors } from '@/observability';
import './leitura-pedido.css';
import { Card } from '@/components/Card/Card';
import { Modal } from '@/components/Modal/Modal';
import { Table } from '@/components/Table/Table';

export const Route = createFileRoute('/_authenticated/movimentacao/leitura-pedido/')({
  component: LeituraPedidoPage,
});

function getMotivoBaixaLabel(pedidoMotivos: IPedidoMotivo[], pedidoMotivoId: string): string {
  const selected = pedidoMotivos.find((m) => String(m.id) === String(pedidoMotivoId));
  return selected?.motivo?.trim() || '';
}

function isMotivoReferenciaPedido(pedidoMotivos: IPedidoMotivo[], pedidoMotivoId: string): boolean {
  if (!pedidoMotivoId) return false;
  const selected = pedidoMotivos.find((m) => String(m.id) === String(pedidoMotivoId));
  if (!selected?.motivo) return false;
  const normalized = selected.motivo
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
  return normalized === 'referencia pedido' || normalized.includes('referencia pedido');
}

function getMessageColor(message: string): string {
  if (message.includes('✅')) return 'green';
  if (message.includes('⚠️')) return 'orange';
  if (message.includes('❌')) return 'red';
  return 'blue';
}

function formatQuantidade(value: number | string | null | undefined): string {
  const numero = typeof value === 'string' ? parseFloat(value) : value;
  if (numero == null || Number.isNaN(numero)) return '0';
  return numero.toLocaleString('pt-BR', { maximumFractionDigits: 3 });
}

const PEDIDO_TABLE_COLUMNS: string[] = [
  'Código Produto',
  'Produto',
  'Quantidade Solicitada',
  'Quantidade Conferida',
  'Quantidade de Caixas',
];

const ETIQUETAS_TABLE_COLUMNS: string[] = [
  'Código de Barras',
  'Código Produto',
  'Descrição',
  'Peso Líquido',
  'Ref. Pedido',
  'Status',
];

const ETIQUETA_REF_PEDIDO_COLUMN = 'Ref. Pedido';

function LeituraPedidoPage() {
  const analytics = useAnalytics();
  const errors = useErrors();
  const [etiquetas, setEtiquetas] = useState<IEtiquetaEscaneada[]>([]);
  const [message, setMessage] = useState('');
  const [pedido, setPedido] = useState('');
  const [operacao, setOperacao] = useState<TOperacaoLeituraPedido>('leitura-pedido');
  const [descricao, setDescricao] = useState('');
  const [barcode, setBarcode] = useState('');
  const barcodeInputRef = useRef<HTMLInputElement>(null);
  const [showPopup, setShowPopup] = useState(false);
  const [numCaixas, setNumCaixas] = useState('');
  const [modalError, setModalError] = useState('');
  const [selectedPedido, setSelectedPedido] = useState<IOrcamento | null>(null);
  const [pedidoMotivo, setPedidoMotivo] = useState('');
  const [referenciaPedidoRetro, setReferenciaPedidoRetro] = useState('');

  const {
    data: pedidoList = [],
    isError: orcamentosError,
    error: orcamentosQueryError,
  } = useQuery({
    queryKey: leituraPedidoKeys.orcamentos,
    queryFn: fetchListaOrcamentos,
  });

  const {
    data: pedidoMotivos = [],
    error: pedidoMotivosQueryError,
  } = useQuery({
    queryKey: leituraPedidoKeys.pedidoMotivos,
    queryFn: fetchPedidoMotivos,
  });

  const pedidoMotivosError = pedidoMotivosQueryError instanceof Error
    ? pedidoMotivosQueryError.message
    : pedidoMotivosQueryError
      ? 'Erro ao carregar motivos de baixa'
      : null;

  useEffect(() => {
    if (orcamentosError) {
      const msg =
        orcamentosQueryError instanceof Error
          ? orcamentosQueryError.message
          : 'Erro ao carregar pedidos';
      setMessage(`Erro ao carregar orçamentos: ${msg}`);
    }
  }, [orcamentosError, orcamentosQueryError]);

  const exigeReferenciaPedidoRetro =
    operacao === 'baixa' && isMotivoReferenciaPedido(pedidoMotivos, pedidoMotivo);

  const mostrarColunaRefPedido =
    operacao === 'baixa' &&
    (exigeReferenciaPedidoRetro || etiquetas.some((e) => e.referencia_pedido_retro));

  useEffect(() => {
    setPedidoMotivo('');
    setReferenciaPedidoRetro('');
  }, [operacao]);

  useEffect(() => {
    if (!exigeReferenciaPedidoRetro) {
      setReferenciaPedidoRetro('');
    }
  }, [exigeReferenciaPedidoRetro]);

  const checkEtiquetaMutation = useMutation({
    mutationFn: checkEtiquetaStatus,
  });

  const finalizeMutation = useMutation({
    mutationFn: executeFinalize,
    onSuccess: (result) => {
      analytics.track('Pedido Finalize Succeeded');
      setMessage(result.message);
      clearFields();
      setShowPopup(false);
    },
    onError: (error: Error) => {
      errors.capture(error, { tags: { source: 'leitura-pedido.finalize' } });
      setMessage(`❌ ${error.message}`);
      setShowPopup(false);
    },
  });

  const handlePedidoChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPedido(e.target.value);
    const pedidoSelecionado = pedidoList.find(
      (p) => p.id_orcamento === Number(e.target.value),
    );

    if (pedidoSelecionado) {
      setSelectedPedido(pedidoSelecionado);
    } else {
      setSelectedPedido(null);
    }
  };

  const handleOperacaoChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const v = e.target.value as TOperacaoLeituraPedido;
    setOperacao(v);
    if (v === 'baixa') {
      setPedido('');
      setSelectedPedido(null);
    }
  };

  const handlePedidoMotivoChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPedidoMotivo(e.target.value);
  };

  const handleBarcodeScan = async () => {
    if (!barcode) {
      setMessage('⚠️ Por favor, insira ou escaneie um código de barras.');
      return;
    }

    if (operacao === 'baixa') {
      if (!pedidoMotivo) {
        setMessage('⚠️ Selecione o tipo de baixa antes de escanear etiquetas.');
        return;
      }
      if (exigeReferenciaPedidoRetro && !referenciaPedidoRetro.trim()) {
        setMessage('⚠️ Informe a referência do pedido antes de escanear etiquetas.');
        return;
      }
    }

    if (etiquetas.some((etiqueta) => etiqueta.barcode === barcode)) {
      setMessage('❌ Erro: Código de barras já foi inserido.');
      return;
    }

    try {
      const result = await checkEtiquetaMutation.mutateAsync(barcode);

      if (result.status === 'DISPONIVEL') {
        setDescricao(result.descricao || 'Descrição não encontrada');

        if (isOperacaoLeituraPedido) {
          const isProductInOrder =
            selectedPedido &&
            selectedPedido.produtos.some(
              (produto) => produto.produto_referencia === result.codigo_produto,
            );

          if (!isProductInOrder) {
            setMessage('❌ Erro: O produto escaneado não faz parte do pedido selecionado.');
            return;
          }

          const pedidoAtual = selectedPedido?.referencia;
          const idPedidoAtual = selectedPedido?.id_orcamento ?? null;

          if (!pedidoAtual) {
            setMessage('⚠️ Por favor, selecione um pedido antes de ler etiquetas.');
            return;
          }

          setEtiquetas((prevEtiquetas) => [
            ...prevEtiquetas,
            {
              barcode,
              descricao: result.descricao || '',
              codigo: result.codigo_produto || '',
              peso: result.peso_liquido || 0,
              referencia: pedidoAtual,
              id_pedido: idPedidoAtual,
              baixada: 'DISPONIVEL',
              status: 'DISPONIVEL',
              codigo_produto: result.codigo_produto || '',
              datasaida: new Date().toISOString(),
            },
          ]);
          setMessage(
            `✅ Etiqueta adicionada com sucesso! Total: ${etiquetas.length + 1} etiquetas`,
          );
        } else if (operacao === 'baixa') {
          const motivoBaixa = getMotivoBaixaLabel(pedidoMotivos, pedidoMotivo);
          setEtiquetas((prevEtiquetas) => [
            ...prevEtiquetas,
            {
              barcode,
              descricao: result.descricao || '',
              codigo: result.codigo_produto || '',
              peso: result.peso_liquido || 0,
              referencia: motivoBaixa,
              referencia_pedido_retro: exigeReferenciaPedidoRetro
                ? referenciaPedidoRetro.trim()
                : null,
              id_pedido: null,
              baixada: 'DISPONIVEL',
              status: 'DISPONIVEL',
              codigo_produto: result.codigo_produto || '',
              datasaida: new Date().toISOString(),
              id_estoque_destino: result.id_estoque_destino,
              lote: result.lote != null ? String(result.lote) : '',
            },
          ]);
          setMessage(
            `✅ Etiqueta adicionada com sucesso! Total: ${etiquetas.length + 1} etiquetas`,
          );
        }
      } else {
        setMessage(`⚠️ Etiqueta já baixada no pedido ${result.pedido}.`);
      }
    } catch (error) {
      errors.capture(error, { tags: { source: 'leitura-pedido.scan' } });
      const msg = error instanceof Error ? error.message : 'Erro desconhecido';
      setMessage(`❌ Erro ao verificar a etiqueta: ${msg}`);
    }

    setBarcode('');
    barcodeInputRef.current?.focus();
  };

  const handleBarcodeInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Tab' || e.key === 'Enter') {
      e.preventDefault();
      void handleBarcodeScan();
    }
  };

  const getQuantidadeCaixasEsperada = (): number => {
    if (operacao === 'baixa') {
      return etiquetas.length;
    }

    return (
      selectedPedido?.produtos?.reduce((sum, produto) => {
        const quantidadeCaixasAutomatica = etiquetas.filter(
          (etiqueta) => etiqueta.codigo_produto === produto.produto_referencia,
        ).length;
        return sum + (produto.quantidadeCaixasManual ?? quantidadeCaixasAutomatica);
      }, 0) ?? 0
    );
  };

  const handleFinalize = () => {
    if (operacao === 'baixa') {
      if (!pedidoMotivo) {
        setMessage('⚠️ Por favor, selecione o tipo de baixa.');
        return;
      }
      if (etiquetas.length === 0) {
        setMessage('⚠️ Adicione pelo menos uma etiqueta antes de finalizar.');
        return;
      }
      if (exigeReferenciaPedidoRetro && !referenciaPedidoRetro.trim()) {
        setMessage('⚠️ Informe a referência do pedido.');
        return;
      }
      setModalError('');
      setNumCaixas('');
      setShowPopup(true);
      return;
    }
    if (!selectedPedido) {
      setMessage('⚠️ Por favor, selecione um pedido.');
      return;
    }
    setModalError('');
    setNumCaixas('');
    setShowPopup(true);
  };

  const buildEtiquetasParaEnvio = (): IEtiquetaEnvio[] => {
    const motivoBaixaLabel =
      operacao === 'baixa' ? getMotivoBaixaLabel(pedidoMotivos, pedidoMotivo) : '';

    return etiquetas.map((etiqueta) => ({
      codigo_barras: etiqueta.barcode,
      baixada: etiqueta.baixada,
      pedido:
        operacao === 'baixa' ? motivoBaixaLabel || etiqueta.referencia : etiqueta.referencia,
      id_pedido: etiqueta.id_pedido,
      descricao: etiqueta.descricao,
      codigo_produto: etiqueta.codigo_produto,
      peso: etiqueta.peso,
      datasaida_date: etiqueta.datasaida,
      referencia_pedido_retro: etiqueta.referencia_pedido_retro ?? null,
    }));
  };

  const handleConfirmFinalize = () => {
    const quantidadeCaixasTotal = getQuantidadeCaixasEsperada();
    const informadas = parseInt(numCaixas, 10);

    if (Number.isNaN(informadas) || informadas !== quantidadeCaixasTotal) {
      setModalError(
        `Número de caixas (${numCaixas || 'vazio'}) não corresponde ao total da conferência (${quantidadeCaixasTotal}).`,
      );
      return;
    }

    setModalError('');
    const etiquetasParaEnvio = buildEtiquetasParaEnvio();

    if (operacao === 'baixa') {
      finalizeMutation.mutate({
        operacao: 'baixa',
        pedidoMotivo,
        etiquetas,
        etiquetasParaEnvio,
      });
      return;
    }

    if (!selectedPedido) {
      setModalError('Pedido inválido ou sem itens.');
      return;
    }

    finalizeMutation.mutate({
      operacao: 'leitura-pedido',
      selectedPedido,
      etiquetas,
      etiquetasParaEnvio,
      quantidadeCaixasTotal,
      tipoOperacao: operacao,
    });
  };

  const clearFields = () => {
    setEtiquetas([]);
    setPedido('');
    setDescricao('');
    setBarcode('');
    setSelectedPedido(null);
    setNumCaixas('');
    setPedidoMotivo('');
    setReferenciaPedidoRetro('');
  };

  const handleStartReading = () => {
    barcodeInputRef.current?.focus();
  };

  const isOperacaoLeituraPedido = operacao === 'leitura-pedido';
  const isOperacaoBaixa = operacao === 'baixa';
  const visibleEtiquetasColumns = mostrarColunaRefPedido
    ? ETIQUETAS_TABLE_COLUMNS
    : ETIQUETAS_TABLE_COLUMNS.filter((column) => column !== ETIQUETA_REF_PEDIDO_COLUMN);

  return (
    <div className="leitura-pedido-container">
      <div className="leitura-pedido-content">
        <Card className="configuracao-operacao">
          <Card.Header><h3>Configuração de Operação</h3></Card.Header>
          <Card.Content>
            <div className="leitura-pedido-grid">
              <div className="input-content entrada-produto-form-grid__field">
                <SelectField
                  id="tipo-operacao"
                  label="Tipo de operação"
                  value={operacao}
                  onChange={handleOperacaoChange}
                  placeholder="Selecione o tipo"
                  options={[
                    { value: 'leitura-pedido', label: 'Leitura de pedidos' },
                    { value: 'baixa', label: 'Baixa' },
                  ]}
                />
              </div>

              {isOperacaoLeituraPedido && (
                <div className="input-content entrada-produto-form-grid__field">
                  <SelectField
                    id="pedido"
                    label="Selecionar Pedido (ERP):"
                    value={pedido}
                    onChange={handlePedidoChange}
                    placeholder="Selecione um pedido"
                    options={pedidoList.map((item) => ({
                      value: item.id_orcamento,
                      label: `${new Date(item.incluido).toLocaleDateString('pt-br')} - ${item.referencia} - ${item.cliente}`,
                    }))}
                  />
                </div>
              )}

              {isOperacaoBaixa && (
                <div className="input-content entrada-produto-form-grid__field">
                  <SelectField
                    id="tipo-baixa"
                    label="Tipo de baixa"
                    value={pedidoMotivo}
                    onChange={handlePedidoMotivoChange}
                    placeholder="Selecione o tipo de baixa"
                    options={pedidoMotivos.map((motivo) => ({
                      value: motivo.id,
                      label: motivo.motivo,
                    }))}
                  />
                </div>
              )}

              {isOperacaoBaixa && (
                <div className="input-content entrada-produto-form-grid__field">
                  <label htmlFor="barcode">Código de Barras:</label>

                  <input
                    type="text"
                    id="barcode"
                    ref={barcodeInputRef}
                    value={barcode}
                    onChange={(e) => setBarcode(e.target.value)}
                    onKeyDown={handleBarcodeInputKeyDown}
                    placeholder="Digite ou escaneie o código de barras"
                  />
                </div>
              )}

              {isOperacaoBaixa && (
                <div className="leitura-pedido-grid__action">
                  <button
                    type="button"
                    className="filter-button"
                    onClick={() => void handleBarcodeScan()}
                  >
                    Adicionar Código de Barras
                  </button>
                </div>
              )}
            </div>
          </Card.Content>
        </Card>

        {isOperacaoLeituraPedido && (
          <Card className="detalhes-pedido">
            <Card.Header>
              <h3>Detalhes do Pedido #{selectedPedido?.referencia || ''}</h3>
            </Card.Header>

            <Card.Content>
              <div className="pedido-observacao">
                <span>OBSERVAÇÃO DO PEDIDO</span>
                <p>{selectedPedido?.observacao || ''}</p>
              </div>

              <div className="pedido-table">
                <Table>
                  <caption className="sr-only">Itens do pedido selecionado</caption>
                  <Table.Header>
                    <Table.Row>
                      {PEDIDO_TABLE_COLUMNS.map((column, index) => (
                        <Table.Head
                          key={index}
                          scope="col"
                        >
                          {column}
                        </Table.Head>
                      ))}
                    </Table.Row>
                  </Table.Header>

                  <Table.Content>
                    {selectedPedido?.produtos.map((produto, index) => {
                      const quantidadeConferidaAutomatica = etiquetas
                        .filter((etiqueta) => etiqueta.codigo_produto === produto.produto_referencia)
                        .reduce((sum, item) => sum + (item.peso || 0), 0);

                      const quantidadeCaixasAutomatica = etiquetas.filter(
                        (etiqueta) => etiqueta.codigo_produto === produto.produto_referencia,
                      ).length;

                      const handleQuantidadeConferidaChange = (
                        e: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        const value = parseFloat(e.target.value) || 0;
                        setSelectedPedido((prev) => {
                          if (!prev) return prev;
                          return {
                            ...prev,
                            produtos: prev.produtos.map((p) =>
                              p.produto_referencia === produto.produto_referencia
                                ? { ...p, quantidadeConferidaManual: value }
                                : p,
                            ),
                          };
                        });
                      };

                      const handleQuantidadeCaixasChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                        const value = parseInt(e.target.value, 10) || 0;
                        setSelectedPedido((prev) => {
                          if (!prev) return prev;
                          return {
                            ...prev,
                            produtos: prev.produtos.map((p) =>
                              p.produto_referencia === produto.produto_referencia
                                ? { ...p, quantidadeCaixasManual: value }
                                : p,
                            ),
                          };
                        });
                      };

                      const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
                        e.target.select();
                      };

                      const quantidadeConferidaFinal =
                        produto.quantidadeConferidaManual ?? quantidadeConferidaAutomatica;

                      const quantidadeCaixasFinal =
                        produto.quantidadeCaixasManual ?? quantidadeCaixasAutomatica;

                      return (
                        <Table.Row key={produto.produto_referencia || index}>
                          <Table.Cell>
                            {produto.produto_referencia || 'Código não disponível'}
                          </Table.Cell>
                          <Table.Cell>
                            {produto.produto_nome || 'Descrição não disponível'}
                          </Table.Cell>
                          <Table.Cell>
                            {formatQuantidade(produto.quantidade)}
                          </Table.Cell>
                          <Table.Cell>
                            <input
                              type="number"
                              className="pedido-table-input"
                              value={quantidadeConferidaFinal}
                              onChange={handleQuantidadeConferidaChange}
                              onFocus={handleFocus}
                              onWheel={(e) => e.currentTarget.blur()}
                            />
                          </Table.Cell>
                          <Table.Cell>
                            <input
                              type="number"
                              className="pedido-table-input"
                              value={quantidadeCaixasFinal}
                              onChange={handleQuantidadeCaixasChange}
                              onFocus={handleFocus}
                              onWheel={(e) => e.currentTarget.blur()}
                            />
                          </Table.Cell>
                        </Table.Row>
                      );
                    })}
                  </Table.Content>
                </Table>
              </div>
            </Card.Content>

            <Card.Footer>
              <div className="input-content entrada-produto-form-grid__field">
                <label htmlFor="barcode">Código de Barras</label>

                <input
                  type="text"
                  id="barcode"
                  ref={barcodeInputRef}
                  value={barcode}
                  onChange={(e) => setBarcode(e.target.value)}
                  onKeyDown={handleBarcodeInputKeyDown}
                  placeholder="Digite ou escaneie o código de barras"
                />
              </div>

              <button
                type="button"
                className="filter-button"
                onClick={() => void handleBarcodeScan()}
              >
                Adicionar Código de Barras
              </button>
            </Card.Footer>
          </Card>
        )}

        {isOperacaoLeituraPedido && (
          <LoadingOrder selectedPedido={selectedPedido} />
        )}

        {isOperacaoBaixa && (
          <Card className="etiquetas-escaneadas">
            <Card.Header>
              <h3>Etiquetas Escaneadas</h3>
            </Card.Header>

            <Card.Content>
              <div className="pedido-table">
                <Table>
                  <caption className="sr-only">Etiquetas escaneadas</caption>
                  <Table.Header>
                    <Table.Row>
                      {visibleEtiquetasColumns.map((column, index) => (
                        <Table.Head
                          key={index}
                          scope="col"
                        >
                          {column}
                        </Table.Head>
                      ))}
                    </Table.Row>
                  </Table.Header>

                  <Table.Content>
                    {etiquetas.map((etiqueta, index) => (
                      <Table.Row key={`${etiqueta.barcode}-${index}`}>
                        <Table.Cell>{etiqueta.barcode}</Table.Cell>
                        <Table.Cell>{etiqueta.codigo}</Table.Cell>
                        <Table.Cell>{etiqueta.descricao}</Table.Cell>
                        <Table.Cell>{formatQuantidade(etiqueta.peso)}</Table.Cell>
                        {mostrarColunaRefPedido && (
                          <Table.Cell>{etiqueta.referencia_pedido_retro || '-'}</Table.Cell>
                        )}
                        <Table.Cell>{etiqueta.status}</Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Content>
                </Table>
              </div>
            </Card.Content>
          </Card>
        )}
      </div>

      <div style={{ textAlign: 'right', display: 'inline' }}>
        {message && (
          <p style={{
              textAlign: 'right',
              borderRadius: '8px',
              fontWeight: 'bold',
              color: getMessageColor(message)
            }}
          >
            {message}
          </p>
        )}

        <button
          type="button"
          className="filter-button"
          onClick={handleFinalize}
        >
          Finalizar Conferência
        </button>
      </div>

      {showPopup && (
        <Modal
          className="leitura-pedido-caixas-modal"
          onClose={() => setShowPopup(false)}
        >
          <Modal.Header
            title="Insira o número de caixas lidas"
            showCloseButton={false}
          />

          <Modal.Content>
            <div className="input-content leitura-pedido-caixas-modal__field">
              <label htmlFor="num-caixas">Número de caixas</label>
              <input
                type="number"
                id="num-caixas"
                className="leitura-pedido-caixas-modal__input"
                value={numCaixas}
                onChange={(e) => {
                  setNumCaixas(e.target.value);
                  setModalError('');
                }}
                placeholder="Informe a quantidade de caixas lidas"
                min={0}
              />
              <p className="leitura-pedido-caixas-modal__hint">
                Esperado: {getQuantidadeCaixasEsperada()} caixa(s)
              </p>
              {modalError && (
                <p className="leitura-pedido-caixas-modal__error">{modalError}</p>
              )}
            </div>
          </Modal.Content>

          <Modal.Footer className="leitura-pedido-caixas-modal__footer">
            <button
              type="button"
              className="filter-button"
              onClick={handleConfirmFinalize}
              disabled={finalizeMutation.isPending}
            >
              {finalizeMutation.isPending ? 'Confirmando...' : 'Confirmar'}
            </button>

            <button
              type="button"
              className="clear-filter-button"
              onClick={() => {
                setModalError('');
                setShowPopup(false);
              }}
            >
              Cancelar
            </button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
}
